import React, { useState, useRef } from 'react';
import { CloseIcon, CartIcon } from './Icons';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import Footer from './Footer';
import BookingDrawer from './BookingDrawer';
import Header from './Header';
import { triggerStockLimitAlert, isCouponExpired } from '../utils/firebaseService';

export default function Checkout({
  view,
  setView,
  isMenuOpen,
  setIsMenuOpen,
  isCartOpen,
  setIsCartOpen,
  isBookingOpen,
  setIsBookingOpen,
  isSearchOpen,
  setIsSearchOpen,
  favorites,
  setFavorites,
  cartItems,
  setCartItems,
  bookingStep,
  setBookingStep,
  bookingForm,
  setBookingForm,
  currentUser,
  setCurrentUser,
  products = [],
  discounts = [],
}) {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [discountObj, setDiscountObj] = useState(null);
  const [shippingForm, setShippingForm] = useState({
    email: '',
    country: '',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    pinCode: '',
    phone: '',
  });

  const getSubtotal = () => {
    return cartItems.reduce((acc, item) => acc + item.price * item.qty, 0);
  };

  const getDiscountAmount = () => {
    if (!discountObj) return 0;
    const sub = getSubtotal();
    if (discountObj.type === 'percentage') {
      return (sub * discountObj.value) / 100;
    }
    return Math.min(sub, discountObj.value);
  };

  const getTotal = () => {
    return Math.max(0, getSubtotal() - getDiscountAmount());
  };

  const handleApplyPromo = (e) => {
    e.preventDefault();
    const clean = promoCode.trim().toUpperCase();
    if (!clean) return;

    const available = (discounts && discounts.length > 0) ? discounts : [
      { code: 'WELCOME10', type: 'percentage', value: 10, status: 'Active' },
      { code: 'FESTIVE20', type: 'percentage', value: 20, status: 'Active' },
      { code: 'KARNNIKARA5K', type: 'fixed', value: 5000, status: 'Active' }
    ];

    const match = available.find(d => d.code.toUpperCase() === clean);
    if (match) {
      if (match.status === 'Inactive') {
        alert(`Coupon code "${clean}" is currently inactive.`);
        return;
      }
      if (isCouponExpired(match)) {
        const expiryStr = match.validUntil ? new Date(match.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'recently';
        alert(`Coupon code "${clean}" has expired on ${expiryStr}.`);
        return;
      }
      setDiscountObj(match);
      const label = match.type === 'percentage' ? `${match.value}%` : `₹${match.value.toLocaleString('en-IN')}`;
      alert(`Promo code "${match.code}" applied! ${label} discount has been subtracted.`);
    } else {
      alert(`Invalid promo code "${clean}".`);
    }
  };

  const handleInputChange = (field, val) => {
    setShippingForm(prev => ({ ...prev, [field]: val }));
  };

  const handleCheckoutSubmit = (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      setShowEmptyMsg(true);
      setTimeout(() => setShowEmptyMsg(false), 3000);
      return;
    }
    setView('payment');
  };

  const [showEmptyMsg, setShowEmptyMsg] = useState(false);
  const [undoItem, setUndoItem] = useState(null);
  const undoTimerRef = useRef(null);

  const handleQtyChange = (id, delta) => {
    setCartItems(prev => {
      const item = prev.find(i => i.id === id);
      if (!item) return prev;
      if (item.qty + delta <= 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoItem(item);
        undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
        return prev.filter(i => i.id !== id);
      }
      const liveProd = (products || []).find(p => String(p.id) === String(id));
      const itemStock = (liveProd && typeof liveProd.stock === 'number')
        ? liveProd.stock
        : ((typeof item.stock === 'number') ? item.stock : 10);
      if (delta > 0 && item.qty + delta > itemStock) {
        triggerStockLimitAlert(itemStock);
        return prev.map(i => i.id === id ? { ...i, qty: itemStock } : i);
      }
      return prev.map(i => i.id === id ? { ...i, qty: i.qty + delta } : i);
    });
  };

  const removeItem = (id) => {
    const item = cartItems.find(i => i.id === id);
    if (item) {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoItem(item);
      undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
    }
    setCartItems(prev => prev.filter(i => i.id !== id));
  };

  const handleUndo = () => {
    if (undoItem) {
      setCartItems(prev => [...prev, { ...undoItem, qty: 1 }]);
      setUndoItem(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    }
  };



  return (
    <div className="bg-noise min-h-screen relative font-sans text-[#584738] flex flex-col antialiased">
      
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* 2. MAIN CHECKOUT SECTION */}
      <main className="max-w-7xl mx-auto px-4 md:px-12 py-10 md:py-12 w-full flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start mt-2">
          
          {/* Left Column: Form details */}
          <section className="flex flex-col">
            {/* Back button */}
            <button
              onClick={() => setView('collections')}
              className="text-[#584738]/60 hover:text-[#584738] text-xs font-sans tracking-widest uppercase flex items-center gap-2 mb-6 transition-colors self-start focus:outline-none"
            >
              <span>&larr;</span> Back to the main page
            </button>

            <h2 className="text-3xl md:text-4.5xl font-serif text-[#584738] tracking-widest font-normal uppercase mb-1.5 leading-none">
              Checkout
            </h2>
            <span className="text-[10px] md:text-[11px] tracking-[0.25em] text-[#584738]/50 uppercase font-sans font-semibold mb-6 block">
              Cart / Shipping
            </span>

            <form onSubmit={handleCheckoutSubmit} className="flex flex-col gap-6">
              
              {/* Email Section */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-end">
                  <label htmlFor="checkout-email" className="text-xs tracking-widest font-sans font-bold uppercase text-[#584738]">
                    Email *
                  </label>
                  <button
                    type="button"
                    onClick={() => alert("Redirecting to login portal...")}
                    className="text-[11px] text-[#584738]/60 hover:text-[#584738] underline font-sans focus:outline-none transition-colors"
                  >
                    Already have an account? Log in
                  </button>
                </div>
                <input
                  id="checkout-email"
                  type="email"
                  placeholder="Enter email here ..."
                  value={shippingForm.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans font-normal"
                  required
                />
              </div>

              {/* Shipping Details */}
              <div className="flex flex-col gap-5 mt-4">
                <h3 className="text-[13px] tracking-widest font-sans font-bold uppercase text-[#584738] border-b border-[#584738]/10 pb-2 mb-1">
                  Shipping Details
                </h3>

                {/* Country dropdown */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="checkout-country" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Country / Region *</label>
                  <select
                    id="checkout-country"
                    value={shippingForm.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] focus:outline-none focus:border-[#584738] font-sans"
                    required
                  >
                    <option value="">Select Country / Region</option>
                    <option value="IN">India</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AE">United Arab Emirates</option>
                  </select>
                </div>

                {/* First & Last name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkout-fname" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">First name *</label>
                    <input
                      id="checkout-fname"
                      type="text"
                      placeholder="First name"
                      value={shippingForm.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkout-lname" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Last name *</label>
                    <input
                      id="checkout-lname"
                      type="text"
                      placeholder="Last name"
                      value={shippingForm.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                      required
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="checkout-address" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Address *</label>
                  <input
                    id="checkout-address"
                    type="text"
                    placeholder="Address"
                    value={shippingForm.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                    required
                  />
                </div>

                {/* Apartment */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="checkout-apt" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Apartment, suite, etc. (optional)</label>
                  <input
                    id="checkout-apt"
                    type="text"
                    placeholder="Apartment, suite, etc. (optional)"
                    value={shippingForm.apartment}
                    onChange={(e) => handleInputChange('apartment', e.target.value)}
                    className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                  />
                </div>

                {/* City, State, Pin Code */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkout-city" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">City *</label>
                    <input
                      id="checkout-city"
                      type="text"
                      placeholder="City"
                      value={shippingForm.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkout-state" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">State *</label>
                    <input
                      id="checkout-state"
                      type="text"
                      placeholder="State"
                      value={shippingForm.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkout-pin" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Pin Code *</label>
                    <input
                      id="checkout-pin"
                      type="text"
                      placeholder="Pin Code"
                      value={shippingForm.pinCode}
                      onChange={(e) => handleInputChange('pinCode', e.target.value)}
                      className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                      required
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="checkout-phone" className="text-[10px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Phone *</label>
                  <input
                    id="checkout-phone"
                    type="tel"
                    placeholder="Phone"
                    value={shippingForm.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-transparent border border-[#584738]/25 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/35 focus:outline-none focus:border-[#584738] font-sans"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 items-stretch sm:items-start mt-8 pb-10">
                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                  <button
                    type="button"
                    onClick={() => setView('collections')}
                    className="border border-[#584738]/30 hover:bg-[#584738]/5 px-10 py-3.5 text-xs font-sans font-bold tracking-widest uppercase transition-colors text-center text-[#584738]"
                  >
                    &larr; Go Back
                  </button>
                  <button
                    type="submit"
                    disabled={cartItems.length === 0}
                    className={`px-12 py-3.5 text-xs font-sans font-bold tracking-widest uppercase transition-colors text-center shadow-xs ${
                      cartItems.length === 0
                        ? 'bg-[#584738]/30 text-white/60 cursor-not-allowed'
                        : 'bg-[#584738] text-white hover:bg-black'
                    }`}
                  >
                    Continue &rarr;
                  </button>
                </div>
                {showEmptyMsg && (
                  <p className="text-[11.5px] font-sans text-red-500/80 tracking-wide flex items-center gap-1.5 animate-fade-in">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    Please add at least 1 item to your cart to continue.
                  </p>
                )}
              </div>

            </form>
          </section>

          {/* Right Column: Order Summary */}
          <aside className="bg-[#CEC1A8] p-5 md:p-8 border border-[#584738]/10 rounded-xs flex flex-col relative lg:sticky lg:top-28 order-first lg:order-last">
            <h3 className="font-serif text-lg tracking-widest font-bold uppercase text-[#584738] border-b border-[#584738]/20 pb-4 mb-6">
              Your Cart
            </h3>

            {/* Cart list scroll area */}
            <div className="max-h-96 overflow-y-auto space-y-6 pr-2 scrollbar-thin flex-1 min-h-0 mb-6">
              {cartItems.length === 0 ? (
                <div className="text-center py-12 text-[#584738]/50 font-serif italic text-sm">
                  Your cart is empty.
                </div>
              ) : (
                cartItems.map((item, idx) => (
                  <div key={`${item.id}-${idx}`} className="flex gap-4 border-b border-[#584738]/5 pb-4 items-start font-serif">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-20 object-cover border border-[#584738]/10 bg-neutral-100 flex-shrink-0"
                    />
                    <div className="flex-1 flex flex-col gap-2">
                      <h4 className="text-xs md:text-sm font-serif font-semibold tracking-wide text-[#584738] leading-tight">
                        {item.name}
                      </h4>
                      <div className="flex items-center justify-between">
                        {/* Qty stepper */}
                        <div className="flex items-center border border-[#584738]/30 select-none font-sans">
                          <button
                            onClick={() => handleQtyChange(item.id, -1)}
                            className="w-7 h-7 flex items-center justify-center text-[#584738] hover:bg-[#584738]/10 transition-colors focus:outline-none text-base leading-none"
                          >−</button>
                          <span className="w-7 text-center text-[12px] font-semibold text-[#584738]">{item.qty}</span>
                          <button
                            onClick={() => handleQtyChange(item.id, 1)}
                            className="w-7 h-7 flex items-center justify-center text-[#584738] hover:bg-[#584738]/10 transition-colors focus:outline-none text-base leading-none"
                          >+</button>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-[12.5px] text-[#584738] font-bold font-sans">
                            Rs. {(item.price * item.qty).toLocaleString('en-IN')}.00
                          </span>
                          <span className="text-[9px] text-[#584738]/60 font-sans font-normal">(incl. of all taxes)</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="self-start text-[10px] font-sans text-[#584738]/45 hover:text-red-500 uppercase tracking-widest transition-colors focus:outline-none"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Promo Code section */}
            <form onSubmit={handleApplyPromo} className="flex gap-3 items-stretch w-full mb-6 pt-2 border-t border-[#584738]/10">
              <input
                type="text"
                placeholder="Promo Code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                className="flex-1 bg-transparent border border-[#584738]/30 px-3.5 py-3 text-xs text-[#584738] placeholder-[#584738]/55 focus:outline-none focus:border-[#584738] uppercase font-sans font-normal"
              />
              <button
                type="submit"
                className="bg-[#584738] text-white hover:bg-black px-6 py-3 text-[11px] tracking-[0.15em] font-sans font-bold uppercase transition-colors"
              >
                Apply
              </button>
            </form>

            {/* Price Calculations */}
            <div className="flex flex-col gap-3.5 border-t border-[#584738]/10 pt-5 font-sans">
              <div className="flex justify-between items-center text-xs">
                <div className="flex flex-col text-left">
                  <span className="text-[#584738] tracking-wider uppercase font-semibold">Subtotal</span>
                  <span className="text-[9.5px] text-[#584738]/60 font-sans normal-case font-normal">(Inclusive of all taxes)</span>
                </div>
                <span className="text-[#584738] font-bold">
                  Rs. {getSubtotal().toLocaleString('en-IN')}.00
                </span>
              </div>
              
              {discountObj && (
                <div className="flex justify-between items-center text-xs text-green-700">
                  <span className="tracking-wider uppercase font-semibold">Coupon ({discountObj.code} - {discountObj.type === 'percentage' ? `${discountObj.value}%` : `Rs. ${discountObj.value}`})</span>
                  <span className="font-bold">
                    - Rs. {getDiscountAmount().toLocaleString('en-IN')}.00
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center text-xs border-b border-[#584738]/10 pb-3">
                <span className="text-[#584738] tracking-wider uppercase font-semibold">Shipping Cost</span>
                <span className="text-[#584738] font-bold tracking-wide uppercase text-[11px]">Free</span>
              </div>

              <div className="flex justify-between items-baseline font-serif mt-1">
                <div className="flex flex-col text-left">
                  <span className="text-[13px] md:text-sm font-bold tracking-widest text-[#584738] uppercase">Total</span>
                  <span className="text-[10px] text-[#584738]/60 font-sans tracking-normal normal-case font-normal">(Inclusive of all taxes)</span>
                </div>
                <span className="text-lg font-extrabold text-[#584738]">
                  Rs. {getTotal().toLocaleString('en-IN')}.00
                </span>
              </div>
            </div>

          </aside>

        </div>

      </main>

      {/* ── FOOTER ── */}
      <Footer setView={setView} />

      {/* --- DRAWERS & MODALS SYNCED WITH STATE --- */}

      {/* A. MOBILE MENU DRAWER */}
      <MenuDrawer isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} setView={setView} setIsBookingOpen={setIsBookingOpen} />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />

      {/* B. LUXURY CART SLIDE-OVER */}
      <div className={`fixed inset-0 z-50 transition-opacity duration-300 pointer-events-none ${isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`}>
        <div onClick={() => setIsCartOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity" />
        <div className={`fixed inset-y-0 right-0 h-full w-full md:w-96 bg-noise shadow-2xl p-6 transition-transform duration-300 flex flex-col ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between border-b border-[#584738]/10 pb-4 mb-6 flex-shrink-0">
            <h3 className="font-serif text-lg tracking-widest font-semibold uppercase flex items-center gap-2 text-[#584738]">
              Your Cart
              <span className="text-xs bg-[#584738]/10 text-[#584738] px-2.5 py-0.5 rounded-full font-sans font-bold">
                {cartItems.reduce((acc, it) => acc + it.qty, 0)}
              </span>
            </h3>
            <button onClick={() => setIsCartOpen(false)} className="text-[#584738] hover:text-[#584738] focus:outline-none" aria-label="Close Cart">
              <CloseIcon />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1 scrollbar-thin">
            {cartItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#584738]/40 font-serif">
                <CartIcon />
                <p className="mt-3 text-sm tracking-wide font-medium">Your cart is empty.</p>
                <button onClick={() => setIsCartOpen(false)} className="text-[#584738] hover:underline text-xs tracking-widest font-semibold uppercase mt-2">Start Shopping</button>
              </div>
            ) : (
              cartItems.map((item, index) => (
                <div key={`${item.id}-${index}`} className="flex gap-4 border-b border-[#584738]/5 pb-4 font-serif">
                  <img src={item.image} alt={item.name} className="w-16 h-20 object-cover border border-[#584738]/5" />
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-serif font-medium tracking-wide text-[#584738]/90 leading-tight">{item.name}</h4>
                      <p className="text-[12.5px] text-[#584738] font-medium mt-1">
                        ₹{item.price.toLocaleString('en-IN')} <span className="text-[9.5px] text-[#584738]/60 font-sans font-normal">(incl. of all taxes)</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-2 font-sans">
                      <div className="flex items-center border border-[#584738]/20 select-none">
                        <button onClick={() => handleQtyChange(item.id, -1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">-</button>
                        <span className="px-3 py-0.5 text-center font-medium bg-[#584738]/2.5">{item.qty}</span>
                        <button onClick={() => handleQtyChange(item.id, 1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">+</button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="text-[#584738]/60 hover:text-red-500 font-semibold">Remove</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="border-t border-[#584738]/10 pt-6 mt-6 flex flex-col gap-4 flex-shrink-0">
              <div className="flex items-center justify-between font-serif">
                <div className="flex flex-col text-left">
                  <span className="text-md font-medium tracking-wide text-[#584738]/80">Subtotal</span>
                  <span className="text-[10px] text-[#584738]/60 font-sans normal-case">(Inclusive of all taxes)</span>
                </div>
                <span className="text-lg font-bold text-[#584738]">₹{getSubtotal().toLocaleString('en-IN')}</span>
              </div>
              <p className="text-[10px] text-[#584738]/60 tracking-wider text-center font-sans">Complimentary Express Shipping • (Inclusive of all taxes)</p>
              <button onClick={() => setIsCartOpen(false)} className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs tracking-widest font-bold uppercase transition-colors shadow-xs font-sans">PROCEED TO CHECKOUT</button>
            </div>
          )}

          {/* ── UNDO TOAST ── */}
          {undoItem && (
            <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-3 bg-[#584738] text-[#F5E6D8] px-4 py-3 rounded-xs animate-fade-in">
              <p className="text-[11.5px] font-sans tracking-wide truncate flex-1">
                <span className="opacity-60 mr-1">Removed:</span>{undoItem.name}
              </p>
              <button
                onClick={handleUndo}
                className="flex items-center gap-1.5 text-[11px] font-sans font-bold tracking-widest uppercase text-[#CEC1A8] hover:text-white transition-colors focus:outline-none flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                Undo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* C. BOOK APPOINTMENT DRAWER */}
      <BookingDrawer
        isOpen={isBookingOpen}
        onClose={() => { setIsBookingOpen(false); setBookingStep(1); }}
        bookingStep={bookingStep}
        setBookingStep={setBookingStep}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
      />

    </div>
  );
}
